/**
 * Keeps an installed app current. The service worker serves the shell
 * cache-first with a background revalidate (public/sw.js), so a page can
 * launch from an old cached "/" while a newer one is already installed or
 * being fetched; the service worker still activates new versions immediately
 * (skipWaiting + claim), a page that is already running keeps its old
 * scripts, and an iOS home-screen app resumed from the background can run
 * stale code for days. So: check for a new worker as soon as this module
 * runs, every time the app comes to the foreground, and on a route change
 * (at most once a minute), and once a new worker has taken control reload at
 * the next safe moment (reloadMoment below); a form, a sheet, a composer or a
 * photo hand-off in progress is never interrupted.
 *
 * A desktop tab is rarely hidden and may sit on one screen for hours (owner,
 * 2026-10-05: "Some features and updates also straight aren't working on web
 * desktop"): waiting for a list screen in the foreground alone left a tab
 * parked on a task on the old build until someone happened to go back to a
 * board. So a pending update also applies at the next route change, onto any
 * screen that is not a form, and while the tab is hidden, where nobody sees
 * the reload.
 */

import { peonSpeaking } from "./audio.ts";
import { hasTypedText } from "./typed-text.ts";

/** The list screens: a reload there in the foreground loses nothing. */
const LIST_ROUTES = /^#\/?(done|schedules|hall|rewards|upgrades)?\/?(\?.*)?$/;
/** The forms (a new task, a schedule new or edited, a new upgrade): never reloaded under the peon's hands. */
const FORM_ROUTES = /^#\/?(new|schedule\/[^/?]+|upgrades\/new)\/?(\?.*)?$/;

/** How often a held reload looks again while the peon is still speaking. */
export const SPEECH_RECHECK_MS = 500;
/** A route change looks for a new worker at most this often. */
export const ROUTE_CHECK_MS = 60_000;

/** When a reload is being considered: the tab showing, the tab hidden, or a route just changed. */
export type UpdateMoment = "visible" | "hidden" | "route";

/**
 * Whether a pending update may reload now. `busy` is true while something on the page would be lost
 * (a sheet or viewer open, a field with text in it, a photo on its way back from the camera).
 * - A form screen: never; the update waits for the peon to leave it.
 * - Anything busy: never.
 * - A list screen in the foreground, as before.
 * - Any other screen at a route change (the screen is being replaced anyway), or while hidden.
 */
export function reloadMoment(hash: string, moment: UpdateMoment, busy: boolean): boolean {
  const h = hash || "#/";
  if (FORM_ROUTES.test(h) || busy) return false;
  if (moment === "hidden" || moment === "route") return true;
  return LIST_ROUTES.test(h);
}

/**
 * True while a reload would lose something: a modal open (a sheet, a viewer, the photo check, the
 * tour), a text field with something typed in it (the note composer, the weekly writing, a rename),
 * or a photo pick under way (src/photo-pick.ts keeps it under "op.photoPick" until the photo lands or
 * the picker is cancelled; on a phone the camera hides the tab, which must not reload it).
 */
export function pageBusy(): boolean {
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem("op.photoPick")) return true;
  } catch {
    /* storage unavailable */
  }
  if (typeof document === "undefined" || typeof document.querySelector !== "function") return false;
  if (document.querySelector("[aria-modal='true'], .sheet-backdrop")) return true;
  return hasTypedText(document);
}

export function watchForUpdates(reg: ServiceWorkerRegistration, speaking: () => boolean = peonSpeaking, busy: () => boolean = pageBusy): void {
  let pending = false;
  let recheck: ReturnType<typeof setTimeout> | null = null;
  let lastCheck = Date.now();
  const check = () => {
    lastCheck = Date.now();
    reg.update().catch(() => {});
  };
  const reloadWhenSafe = (moment: UpdateMoment) => {
    if (recheck !== null) {
      clearTimeout(recheck);
      recheck = null;
    }
    if (!pending) return;
    const now: UpdateMoment = document.visibilityState === "visible" ? moment : "hidden";
    if (!reloadMoment(location.hash, now, busy())) return;
    // Job's Done lands on the board with its completion line still loading or playing; a reload
    // then would cut the line (the owner heard nothing, 2026-09-24). Let the peon finish first.
    if (now !== "hidden" && speaking()) {
      recheck = setTimeout(() => reloadWhenSafe(moment), SPEECH_RECHECK_MS);
      return;
    }
    pending = false;
    location.reload();
  };

  // A controller that changes while we already had one means a new version took over.
  let hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    pending = true;
    reloadWhenSafe("visible");
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") {
      reloadWhenSafe("hidden");
      return;
    }
    check();
    reloadWhenSafe("visible");
  });
  window.addEventListener("hashchange", () => {
    if (Date.now() - lastCheck >= ROUTE_CHECK_MS) check();
    reloadWhenSafe("route");
  });

  // Also check right away: an app resumed after days away should not wait
  // for the next visibilitychange (it may already be visible) before it
  // looks for a fresher shell.
  check();
}
